"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.tryDeleteMarkup = exports.reducer = exports.getSheetsState = exports.getSheetByUrn = exports.getSheetBySheetId = exports.getSheetAssociationByIssueId = exports.fetchSheetsSuccess = exports.fetchSheets = exports.fetchSheetAssociationSuccess = exports.fetchIssuesSheetAssociation = exports.deleteSheet = exports.deleteMarkup = exports.VALID_SURFACE_TYPES = exports.STATE_KEY = exports.SHEET_SURFACE_TYPE = exports.DOCUMENT_SURFACE_TYPE = void 0;
var _toolkit = require("@reduxjs/toolkit");
var _DocsApi = require("../../../docs-service/DocsApi");
var _documents = require("./documents");
const SHEET_SURFACE_TYPE = exports.SHEET_SURFACE_TYPE = "acc_sheet";
const DOCUMENT_SURFACE_TYPE = exports.DOCUMENT_SURFACE_TYPE = "acc_document";
const VALID_SURFACE_TYPES = exports.VALID_SURFACE_TYPES = [SHEET_SURFACE_TYPE, DOCUMENT_SURFACE_TYPE];

// we re-export the below types, since they are be used elsewhere
// and the consuming context shouldn't need to know they are from the API

const initialState = {
  sheetAssociationMap: {},
  sheetsMap: {},
  sheetsByUrn: {}
};
const sheetsSlice = (0, _toolkit.createSlice)({
  name: "sheetAssocation",
  initialState,
  reducers: {
    fetchSheetAssociationSuccess: (state, action) => {
      state.sheetAssociationMap = {
        ...state.sheetAssociationMap,
        ...action.payload.sheetAssociationMap
      };
    },
    fetchSheetsSuccess: (state, action) => {
      action.payload.sheets.forEach(sheet => {
        state.sheetsMap[sheet.id] = sheet;
        state.sheetsByUrn[sheet.bubbleUrn] = sheet;
      });
    },
    deleteSheet: (state, action) => {
      state.sheetAssociationMap[action.payload.issueId] = null;
    }
  }
});
const {
  fetchSheetAssociationSuccess,
  fetchSheetsSuccess,
  deleteSheet
} = sheetsSlice.actions;
exports.deleteSheet = deleteSheet;
exports.fetchSheetsSuccess = fetchSheetsSuccess;
exports.fetchSheetAssociationSuccess = fetchSheetAssociationSuccess;
const reducer = exports.reducer = sheetsSlice.reducer;
const STATE_KEY = exports.STATE_KEY = sheetsSlice.name;
const getSheetsState = state => state[STATE_KEY];
exports.getSheetsState = getSheetsState;
const getSheetAssociationByIssueId = state => issueId => {
  if (!issueId) {
    return undefined;
  }
  const sheetsState = getSheetsState(state);
  return sheetsState.sheetAssociationMap[issueId];
};
exports.getSheetAssociationByIssueId = getSheetAssociationByIssueId;
const getSheetBySheetId = state => sheetId => {
  if (!sheetId) {
    return undefined;
  }
  const sheetsState = getSheetsState(state);
  return sheetsState.sheetsMap[sheetId];
};
exports.getSheetBySheetId = getSheetBySheetId;
const getSheetByUrn = state => sheetUrn => {
  if (!sheetUrn) {
    return undefined;
  }
  const sheetsState = getSheetsState(state);
  return sheetsState.sheetsByUrn[sheetUrn];
};

/**
 * Makes the request to populate the store with a map of
 * issue ids to their associated sheet and markup uids, or null
 */
exports.getSheetByUrn = getSheetByUrn;
const fetchIssuesSheetAssociation = _ref => {
  let {
    issues,
    includeDeletedMarkups = false,
    useCache = false
  } = _ref;
  return async (dispatch, getState, _ref2) => {
    let {
      sdk
    } = _ref2;
    let sheetAssociationMap;
    const fetchedSheetAssociationMap = getSheetsState(getState()).sheetAssociationMap;
    const issueUids = issues.map(issue => issue.id);
    const isAlreadyFetched = issueUids.every(uid => uid in fetchedSheetAssociationMap);
    if (useCache && isAlreadyFetched || !issueUids.length) {
      return;
    }
    try {
      // if includeDeletedMarkups === true, we are in deleted space.
      // This means we need to use the deletedAfter flag.
      // Each issue has a different deletedAfter value.
      // Therefore, we have to send multiple requests, one request per one issue.
      if (includeDeletedMarkups) {
        const sheetAssociations = await Promise.all(issues.map(issue => {
          return sdk.IssueMarkups.batchGet([issue.id], {
            deletedAfter: issue.deletedAt,
            includeDeleted: includeDeletedMarkups
          });
        }));
        sheetAssociationMap = sheetAssociations.reduce((acc, sheetAssociation) => ({
          ...acc,
          ...sheetAssociation
        }),
        // use the fetchedSheetAssociationMap as a staring value so we won't delete previously fetched sheet associations after this accumulator is done running.
        fetchedSheetAssociationMap);
        // if includeDeletedMarkups === false, we can send the batchGet request with an array of issueIds, to save time.
      } else {
        sheetAssociationMap = await sdk.IssueMarkups.batchGet(issueUids, {
          deletedAfter: undefined,
          includeDeleted: includeDeletedMarkups
        });
      }
    } catch (err) {
      // currently we don't do anything if this fails.
      // for completeness, we don't need to get too tricky about this,
      // but we will eventually want the sheetUid for an issue to be either:
      //  - loading (maybe undefined or a symbol?)
      //  - null (loaded, no association)
      //  - a sheet uid (string)
      //  - a failure token (symbol or string?)
      return;
    }
    dispatch(fetchSheetAssociationSuccess({
      sheetAssociationMap
    }));
    const sheetAssociations = Object.values(sheetAssociationMap ? sheetAssociationMap : {}).filter(Boolean);
    const sheetIds = sheetAssociations.filter(sheetAssociation => sheetAssociation.surfaceType === SHEET_SURFACE_TYPE).map(_ref3 => {
      let {
        sheetId
      } = _ref3;
      return sheetId;
    });
    if (sheetIds.length) {
      // need to await this in order to get an indication when we have all the sheets, so we can safely remove the placement loader (otherwise, the placement can't show the correct sheet name)
      await dispatch(fetchSheets({
        sheetIds,
        useCache: true
      }));
    }
    const documentVersionIds = sheetAssociations.filter(sheetAssociation => sheetAssociation.surfaceType === DOCUMENT_SURFACE_TYPE).map(_ref4 => {
      let {
        documentVersionId
      } = _ref4;
      return documentVersionId;
    });
    if (documentVersionIds.length) {
      await dispatch(
      // need to await this in order to get an indication when we have all the files, so we can safely remove the placement loader (otherwise, the placement can't show the correct file name)
      (0, _documents.fetchDocumentsByDocumentVersionIds)({
        documentVersionIds,
        useCache: true
      }));
    }
  };
};

/**
 * Makes the request to populate the store with a map of
 * sheet ids to the sheet data
 */
exports.fetchIssuesSheetAssociation = fetchIssuesSheetAssociation;
const fetchSheets = _ref5 => {
  let {
    sheetIds,
    useCache = false,
    onError
  } = _ref5;
  return async (dispatch, getState, _ref6) => {
    let {
      sdk
    } = _ref6;
    let sheets;
    const fetchedSheets = getSheetsState(getState()).sheetsMap;
    const isAlreadyFetched = sheetIds.every(uid => uid in fetchedSheets);
    if (useCache && isAlreadyFetched) {
      return;
    }
    try {
      sheets = await (0, _DocsApi.getDocsApi)().Sheets.getSheetsByIds(sdk.projectId, [...new Set(sheetIds.filter(Boolean))]);
    } catch (err) {
      // currently we don't do anything if this fails.
      // but for disambiguating from a cache hit for testing,
      // and to allow notifications, we call an onError function
      onError && onError(err);
      return;
    }
    dispatch(fetchSheetsSuccess({
      sheets
    }));
  };
};
exports.fetchSheets = fetchSheets;
const deleteMarkup = _ref7 => {
  let {
    issueId
  } = _ref7;
  return async (dispatch, getState, _ref8) => {
    let {
      sdk
    } = _ref8;
    const sheetsState = getSheetsState(getState());
    const sheetAssociation = sheetsState.sheetAssociationMap[issueId];
    if (sheetAssociation && sheetAssociation.markupId) {
      await sdk.IssueMarkups.delete(sheetAssociation.markupId);
      dispatch(deleteSheet({
        issueId
      }));
    }
  };
};
exports.deleteMarkup = deleteMarkup;
const tryDeleteMarkup = _ref9 => {
  let {
    issueId
  } = _ref9;
  return async () => {
    try {
      await deleteMarkup({
        issueId
      });
    } catch (err) {
      // Currently, we don't do anything if the markup deletion fails,
      // because orphaned markups are OK and the deletion of markups is hidden to the user.
      console.error("Failed to delete the markup");
      return false;
    }
    return true;
  };
};
exports.tryDeleteMarkup = tryDeleteMarkup;
//# sourceMappingURL=sheets.js.map